import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../Provider';
import {
  getStoredConsent,
  updateConsent,
  acceptAllCookies
} from '../../utils/cookieConsentManager';
import './CookiePreferencesModal.css';

/**
 * Cookie Preferences Modal Component
 *
 * Allows users to customize their cookie preferences with granular control.
 * Displays 4 cookie categories with toggle switches.
 *
 * Categories:
 * - Essential: Always active (auth, language, theme, maps)
 * - Functional: Optional, enabled by default (saved locations)
 * - Analytics: Optional, disabled by default (future Google Analytics)
 * - Marketing: Optional, disabled by default (future Facebook Pixel)
 */
const CookiePreferencesModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);

  // Load current preferences
  const [preferences, setPreferences] = useState({
    functional: true,  // Default: enabled
    analytics: false,  // Default: disabled
    marketing: false   // Default: disabled
  });

  // Load saved preferences on mount
  useEffect(() => {
    const consent = getStoredConsent();
    if (consent) {
      setPreferences({
        functional: consent.functional ?? true,
        analytics: consent.analytics ?? false,
        marketing: consent.marketing ?? false
      });
    }
  }, []);

  /**
   * Handle category toggle
   */
  const handleToggle = (category) => {
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  /**
   * Handle "Save Preferences" action
   * Updates consent with user's choices
   */
  const handleSavePreferences = async () => {
    try {
      await updateConsent(preferences, user);
      onClose(true); // true = consent was given
      console.log('Cookie preferences saved:', preferences);
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  };

  /**
   * Handle "Accept All" action
   * Enables all categories and closes modal
   */
  const handleAcceptAll = async () => {
    try {
      await acceptAllCookies(user);
      onClose(true); // true = consent was given
      console.log('User accepted all cookies from modal');
    } catch (error) {
      console.error('Error accepting all cookies:', error);
    }
  };

  /**
   * Handle modal close without saving
   */
  const handleClose = () => {
    onClose(false); // false = no consent change
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  /**
   * Handle escape key
   */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="cookie-modal-backdrop"
          onClick={handleBackdropClick}
        >
          <motion.div
            className="cookie-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="cookie-modal-header">
              <h2 className="cookie-modal-title">
                {t('cookiePreferences.title')}
              </h2>
              <button
                className="cookie-modal-close"
                onClick={handleClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Subtitle */}
            <p className="cookie-modal-subtitle">
              {t('cookiePreferences.subtitle')}
            </p>

            {/* Cookie Categories */}
            <div className="cookie-modal-body">
              {/* Essential Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h3 className="cookie-category-name">
                      {t('cookiePreferences.categories.essential.name')}
                    </h3>
                    <p className="cookie-category-description">
                      {t('cookiePreferences.categories.essential.description')}
                    </p>
                  </div>
                  <div className="cookie-category-toggle">
                    <span className="cookie-category-always-active">
                      {t('cookiePreferences.categories.essential.alwaysActive')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h3 className="cookie-category-name">
                      {t('cookiePreferences.categories.functional.name')}
                    </h3>
                    <p className="cookie-category-description">
                      {t('cookiePreferences.categories.functional.description')}
                    </p>
                  </div>
                  <div className="cookie-category-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={() => handleToggle('functional')}
                        aria-label={t('cookiePreferences.categories.functional.name')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h3 className="cookie-category-name">
                      {t('cookiePreferences.categories.analytics.name')}
                    </h3>
                    <p className="cookie-category-description">
                      {t('cookiePreferences.categories.analytics.description')}
                    </p>
                  </div>
                  <div className="cookie-category-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handleToggle('analytics')}
                        aria-label={t('cookiePreferences.categories.analytics.name')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h3 className="cookie-category-name">
                      {t('cookiePreferences.categories.marketing.name')}
                    </h3>
                    <p className="cookie-category-description">
                      {t('cookiePreferences.categories.marketing.description')}
                    </p>
                  </div>
                  <div className="cookie-category-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handleToggle('marketing')}
                        aria-label={t('cookiePreferences.categories.marketing.name')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with action buttons */}
            <div className="cookie-modal-footer">
              <button
                className="cookie-modal-btn cookie-modal-btn-secondary"
                onClick={handleSavePreferences}
              >
                {t('cookiePreferences.savePreferences')}
              </button>
              <button
                className="cookie-modal-btn cookie-modal-btn-primary"
                onClick={handleAcceptAll}
              >
                {t('cookiePreferences.acceptAll')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CookiePreferencesModal;
